import axios from "axios";
import { memo, useCallback, useEffect, useState } from "react";
import { FaPhoneAlt } from "react-icons/fa"; // Import phone icon for call button
import { MdOutlineBookmarkAdd } from "react-icons/md";
import { RiDeleteBin6Line } from "react-icons/ri";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { logout } from "../../redux/authSlice";
import DeleteConfirmationModal from "../DeleteConfirmationModal/DeleteConfirmationModal";
import styles from "../InboundTemplate/InboundTemplate.module.css";
import LoaderSpiner from "../LoaderSpiner";

const InboundTemplate = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { token, userId, isAuthenticated } = useSelector((state) => state.auth);
  const [agents, setAgents] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [agentToDelete, setAgentToDelete] = useState(null);
  const [error, setError] = useState(null);
  const [isPlaygroundOpen, setIsPlaygroundOpen] = useState(false); // State for popup
  const [selectedAgent, setSelectedAgent] = useState(""); // State for selected agent
  const [isCalling, setIsCalling] = useState(false); // State for call status
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  const [linkingAgent, setLinkingAgent] = useState(null);
  const [appNameInput, setAppNameInput] = useState("");
  const [unlinkingAgentId, setUnlinkingAgentId] = useState(null);

  useEffect(() => {
    if (!token || !userId || !isAuthenticated) {
      console.error("InboundTemplate: Missing or invalid token/userID");
      dispatch(logout());
      navigate("/login");
      return;
    }

    const fetchData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const response = await axios.get(
          `https://prod.toxboxai.com/api/agent-creation/inbound-by-creator/${userId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (response.status === 401) {
          console.warn("InboundTemplate: Unauthorized, redirecting to login");
          dispatch(logout());
          navigate("/login");
          return;
        } else if (response.status === 404) {
          console.warn("InboundTemplate: No agents found");
          setAgents([]);
        } else if (response.status !== 200) {
          throw new Error(
            `HTTP error: ${response.status} - ${response.statusText}`
          );
        }

        const result = response.data;
        console.log("InboundTemplate: Fetched agents:", result.agents);
        const inboundAgents = result.agents.filter(
          (agent) => agent.type === "inbound"
        );
        setAgents(inboundAgents || []);
      } catch (error) {
        console.error("InboundTemplate: Error fetching data:", error);
        setError(
          error.response?.data?.error ||
          "Failed to load data. Please try again later."
        );
        setAgents([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [navigate, dispatch, token, userId, isAuthenticated]);

  const handleDelete = (agentId) => {
    const agent = agents.find((a) => a._id === agentId);
    if (!agent) {
      console.error("InboundTemplate: Agent not found for deletion:", agentId);
      return;
    }
    setAgentToDelete(agent);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!agentToDelete) {
      console.error("InboundTemplate: No agent selected for deletion");
      return;
    }

    if (!token || !isAuthenticated) {
      alert("Authentication failed. Please log in again.");
      dispatch(logout());
      navigate("/login");
      return;
    }

    try {
      await axios.delete(
        `https://prod.toxboxai.com/api/agent-creation/delete/${agentToDelete._id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      setAgents((prevAgents) =>
        prevAgents.filter((agent) => agent._id !== agentToDelete._id)
      );
      console.log(
        "InboundTemplate: Agent deleted successfully:",
        agentToDelete._id
      );
    } catch (error) {
      console.error("InboundTemplate: Error deleting agent:", error);
      if (error.response?.status === 401) {
        alert("Session expired. Please log in again.");
        dispatch(logout());
        navigate("/login");
      } else {
        alert(
          error.response?.data?.error ||
          "Failed to delete agent. Please try again."
        );
      }
    } finally {
      setIsDeleteModalOpen(false);
      setAgentToDelete(null);
    }
  };

  const handleEditData = (agent) => {
    try {
      if (!agent?._id) {
        console.error("InboundTemplate: Invalid agent ID:", agent);
        alert("Cannot edit agent: Invalid agent ID.");
        return;
      }
      console.log("InboundTemplate: Navigating to edit agent:", agent);
      const agentData = {
        _id: agent._id,
        avatarName: agent.avatarName || "Unnamed Agent",
        phoneNumber: agent.phoneNumber || "",
        avatarImage: agent.avatarImage || "https://i.pravatar.cc/40",
        language: agent.language || "en-US",
        gender: agent.gender || "",
        voice: agent.voice?.displayName || agent.voice || "",
        templateId: agent.templateId || "",
        systemPrompt:
          typeof agent.systemPrompt === "object"
            ? agent.systemPrompt?.promptName || ""
            : agent.systemPrompt || "",
        model: agent.model || "fixie-ai/ultravox",
        temperature: agent.temperature || 0.3,
        firstSpeaking: agent.firstSpeaking || false,
        welcomeMessage: agent.firstSpeakerSettings?.agent?.welcomeMessage || "",
        maxDuration: agent.maxDuration
          ? agent.maxDuration.replace("s", "")
          : "",
        timeExceedMessage: agent.timeExceedMessage || "",
        transcript: agent.transcript !== undefined ? agent.transcript : false,
        callRecording:
          agent.callRecording !== undefined ? agent.callRecording : false,
        knowledgeBaseId: agent.knowledgeBaseId || "",
        type: agent.type || "inbound",
      };
      navigate(`/inbound/edit-agent/${agent._id}`, {
        state: { agent: agentData },
      });
    } catch (error) {
      console.error("InboundTemplate: Error navigating to edit page:", error);
      alert("Failed to navigate to edit page.");
    }
  };

  const handleCreateAgent = () => {
    try {
      console.log("InboundTemplate: Navigating to create agent");
      navigate("/inbound/createagent");
    } catch (error) {
      console.error(
        "InboundTemplate: Error navigating to create agent:",
        error
      );
      alert("Failed to navigate to create agent page.");
    }
  };

  const handlePlaygroundOpen = () => {
    setIsPlaygroundOpen(true);
  };

  const handlePlaygroundClose = () => {
    setIsPlaygroundOpen(false);
    setSelectedAgent("");
    setIsCalling(false);
  };

  const handleAgentSelect = (e) => {
    setSelectedAgent(e.target.value);
  };

  const handleCallStart = () => {
    if (!selectedAgent) {
      alert("Please select an agent before starting a call.");
      return;
    }
    setIsCalling(true);
    // Simulate a call start (replace with actual call logic)
    console.log(`Starting call with agent: ${selectedAgent}`);
    // Example: Simulate call duration
    setTimeout(() => {
      setIsCalling(false);
      console.log("Call ended");
    }, 5000); // Simulate 5-second call
  };

  const handleLinkNumber = useCallback(async () => {
    if (!appNameInput.trim() || !linkingAgent?._id) {
      toast.error("App Name and Agent are required!");
      return;
    }

    console.log("🔗 Linking agent object:", linkingAgent);
    console.log("🔗 Linking agent ID:", linkingAgent?._id);

    const payload = {
      appName: appNameInput,
      agentId: linkingAgent._id,
    };
    console.log("📤 Sending payload:", payload);

    try {
      const response = await axios.post(
        "https://prod.toxboxai.com/api/call-plivo-inbound/linked-number",
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      toast.success(response.data.message || "Number linked successfully!");

      setAgents((prev) =>
        prev.map((a) =>
          a._id === linkingAgent._id
            ? {
              ...a,
              linkedNumber: response.data.linkedNumber,
              isLinkedNumberReady: response.data.isLinkedNumberReady,
            }
            : a
        )
      );

      setIsLinkModalOpen(false);
      setAppNameInput("");
      setLinkingAgent(null);
    } catch (error) {
      console.error("Link Number Error:", error);
      toast.error(
        error.response?.data?.error ||
        error.message ||
        "Failed to link number. Try again."
      );
    }
  }, [appNameInput, linkingAgent, token]);

  const handleUnlinkNumber = useCallback(
    async (agent) => {
      if (!agent?._id) {
        toast.error("Agent ID is required!");
        return;
      }

      console.log("🔓 Unlinking number for agent:", agent?._id);
      setUnlinkingAgentId(agent?._id); // 🔹 set loading state

      try {
        const response = await axios.post(
          "https://prod.toxboxai.com/api/call-plivo-inbound/unlink-number",
          { agentId: agent._id },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        toast.success(response.data.message || "Number unlinked successfully!");

        setAgents((prev) =>
          prev.map((a) =>
            a._id === agent._id
              ? {
                ...a,
                linkedNumber: response.data.linkedNumber,
                isLinkedNumberReady: response.data.isLinkedNumberReady,
              }
              : a
          )
        );
      } catch (error) {
        console.error("Unlink Number Error:", error);
        toast.error(
          error.response?.data?.error ||
          error.message ||
          "Failed to unlink number. Try again."
        );
      } finally {
        setUnlinkingAgentId(null); // 🔹 reset loading
      }
    },
    [token]
  );

  // const LinkNumberPopupModel = ({
  //   isOpen,
  //   onClose,
  //   appNameInput,
  //   setAppNameInput,
  //   handleLinkNumber,
  // }) => {
  //   if (!isOpen) return null; // render nothing if modal is closed

  //   return (
  //     <div className={styles.playgroundModal}>
  //       <div className={styles.playgroundContent}>
  //         <button
  //           className={styles.closeButton}
  //           onClick={onClose}
  //           aria-label="Close modal"
  //         >
  //           &times;
  //         </button>
  //         <h3 className={styles.playgroundTitle}>Link Number</h3>
  //         <input
  //           type="text"
  //           placeholder="Enter a unique application name"
  //           className={styles.agentSelect}
  //           value={appNameInput}
  //           onChange={(e) => setAppNameInput(e.target.value)}
  //         />
  //         <button
  //           onClick={handleLinkNumber}
  //           className={styles.callButton}
  //           disabled={!appNameInput.trim()}
  //         >
  //           Submit
  //         </button>
  //       </div>
  //     </div>
  //   );
  // };

  return (
    <div className={styles.container}>
      <div className={styles.textWrapper}>
        <p className={styles.heading1}>Build Your Smart Calling Assistant</p>
        <p className={styles.subheading}>
          Ready to build your AI agent or update an existing one? Choose the
          voice, tone, and
          <br />
          personality that fits your brand. It’s quick, easy, and all about your
          needs!
        </p>
      </div>

      <h3 className={styles.heading2}>Inbound Template</h3>
      {/* <button
        onClick={handlePlaygroundOpen}
        className={styles.playgroundButton}
      >
        Open Playground
      </button> */}

      {isLoading ? (
        <LoaderSpiner />
      ) : (
        <div className={styles.grid}>
          <div className={styles.addNew} onClick={handleCreateAgent}>
            <span className={styles.plus}>+</span>
          </div>

          {error && <p className={styles.error}>{error}</p>}

          {Array.isArray(agents) && agents.length > 0 ? (
            agents.map((agent, index) => {
              const topColorClass =
                index % 2 === 0 ? styles.greenBar : styles.blueBar;

              return (
                <div key={agent._id} className={styles.agentCard}>
                  <div className={`${styles.topBar} ${topColorClass}`}>
                    {/* <img
                      src={agent.avatarImage || "https://i.pravatar.cc/40"}
                      alt={agent.avatarName || "Unnamed Agent"}
                      className={styles.avatar}
                    /> */}
                  </div>
                  <h3 className={styles.agentName}>
                    {agent.avatarName || "Unnamed Agent"}
                  </h3>
                  <p className={styles.agentDescription}>
                    {agent.phoneNumber || "No Phone Number"}
                    <br />
                    Language: {agent.language || "Unknown"}
                    <br />
                    Voice:{" "}
                    {agent.voice?.displayName || agent.voice || "Unknown Voice"}
                    <br />
                    Template: {agent.templateId || "No Template Assigned"}
                    <br />
                    Prompt:{" "}
                    {typeof agent.systemPrompt === "object"
                      ? agent.systemPrompt?.promptName || "No Prompt"
                      : agent.systemPrompt || "No Prompt"}
                  </p>

                  <div className={styles.actions}>
                    <button
                      onClick={() => handleEditData(agent)}
                      className={styles.role}
                    >
                      Edit
                    </button>

                    <button
                      onClick={() => {
                        if (agent.isLinkedNumberReady) {
                          // 🔹 If already linked → Unlink
                          handleUnlinkNumber(agent);
                        } else {
                          // 🔹 If not linked → Open modal to link
                          setLinkingAgent(agent);
                          setIsLinkModalOpen(true);
                        }
                      }}
                      className={`${styles.role} ${agent.isLinkedNumberReady ? styles.unlink : styles.link
                        }`}
                      disabled={unlinkingAgentId === agent._id}
                    >
                      {unlinkingAgentId === agent._id
                        ? "Processing..."
                        : agent.isLinkedNumberReady
                          ? "Unlink Number ❌"
                          : "Link Number 🔗"}
                    </button>
                  </div>

                  <div className={styles.icons}>
                    <span
                      className={styles.icon}
                      onClick={() => handleDelete(agent._id)}
                      title="Delete Agent"
                    >
                      <RiDeleteBin6Line />
                    </span>
                    <span className={styles.icon} title="Bookmark Agent">
                      <MdOutlineBookmarkAdd />
                    </span>
                  </div>
                </div>
              );
            })
          ) : (
            <p className={styles.subheading}>
              No inbound agents found. Create a new agent to get started!
            </p>
          )}
        </div>
      )}

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setAgentToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        agentName={agentToDelete?.avatarName || "Unnamed Agent"}
      />

      {isPlaygroundOpen && (
        <div className={styles.playgroundModal}>
          <div className={styles.playgroundContent}>
            <button
              className={styles.closeButton}
              onClick={handlePlaygroundClose}
            >
              &times;
            </button>
            <h3 className={styles.playgroundTitle}>Call Playground</h3>
            <select
              value={selectedAgent}
              onChange={handleAgentSelect}
              className={styles.agentSelect}
              disabled={isCalling}
            >
              <option value="">Select an Agent</option>
              {agents.map((agent) => (
                <option key={agent._id} value={agent._id}>
                  {agent.avatarName || "Unnamed Agent"}
                </option>
              ))}
            </select>
            <input
              type="number"
              placeholder="Enter the number"
              className={styles.agentSelect}
            ></input>

            {isCalling && (
              <div className={styles.waveContainer}>
                <div className={styles.wave}></div>
                <div className={styles.wave}></div>
                <div className={styles.wave}></div>
              </div>
            )}
            <button
              onClick={handleCallStart}
              className={styles.callButton}
              disabled={isCalling || !selectedAgent}
            >
              <FaPhoneAlt /> Start Call
            </button>
          </div>
        </div>
      )}

      {isLinkModalOpen && (
        <LinkNumberPopupModel
          isOpen={isLinkModalOpen}
          onClose={() => {
            setIsLinkModalOpen(false);
            setAppNameInput("");
            setLinkingAgent(null);
          }}
          appNameInput={appNameInput}
          setAppNameInput={setAppNameInput}
          handleLinkNumber={handleLinkNumber}
        />
      )}

      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default InboundTemplate;

const LinkNumberPopupModel = memo(function LinkNumberPopupModel({
  isOpen,
  onClose,
  appNameInput,
  setAppNameInput,
  handleLinkNumber,
}) {
  if (!isOpen) return null;

  return (
    <div className={styles.playgroundModal}>
      <div className={styles.playgroundContent}>
        <button
          className={styles.closeButton}
          onClick={onClose}
          aria-label="Close modal"
        >
          &times;
        </button>
        <h3 className={styles.playgroundTitle}>Link Number</h3>
        <input
          type="text"
          placeholder="Enter a unique application name"
          className={styles.agentSelect}
          value={appNameInput}
          onChange={(e) => setAppNameInput(e.target.value)}
          autoFocus
        />
        <button
          onClick={handleLinkNumber}
          className={styles.callButton}
          disabled={!appNameInput.trim()}
        >
          Submit
        </button>
      </div>
    </div>
  );
});
